<template>
  <div v-if="data">
    <div class="container container-fluid">
      <h3 class="pull-left text-muted">Registrera minderårigt barn <span v-if="indexPatientName">(indexpatient {{indexPatientName}})</span></h3>
      <div class="clearfix"></div>
      <!-- Skapa ny -->
      <div class="card journal-card">
        <div class="card-body">
          <p class="text-dark pull-left"><strong>Nytt barn</strong></p>
          <div class="row">
            <div class="col-sm-4">
              <div class="form-group">
                <label class="pull-left" for="ssn">Personnummer:</label>
                <input type="number" placeholder="YYYYMMDDXXXX"
                  class="form-control" id="ssn" v-model="ssn" minlength="12" >
              </div>
              <button
                class="btn btn-primary btn-sm"
                @click="getFromNavet()"
                :disabled="!validSsn">Hämta från NAVET
              </button>
              <br />
              <small class="text-muted">
                Fyll i personnummer i formatet <i>YYYYMMDDXXXX</i> <br />
                för hämtning från NAVET
              </small>
            </div>
            <div class="col-sm-4">
              <div class="form-group">
                <label class="pull-left" for="lastname">Efternamn:</label>
                <input type="text" class="form-control" id="lastname" v-model="lastname">
              </div>
            </div>
            <div class="col-sm-4">
              <div class="form-group">
                <label class="pull-left" for="firstname">Tilltalsnamn:</label>
                <input type="text" class="form-control" id="firstname" v-model="firstname">
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="clearfix"></div>
      <!-- Kontaktuppgifter -->
      <div class="card journal-card journal-card-caregiver">
        <div class="card-body">
          <p class="text-dark pull-left"><strong>Kontaktuppgifter till den andra vårdnadshavaren</strong></p>
          <div class="row">
            <div class="col-sm-4" v-if="secondaryCaregivers && secondaryCaregivers.length > 0">
              <label for="secondarycaregiver">Om samma vårdnadshavare för annat barn:</label>
              <select
                class="form-control"
                :class="(email || phonenumber) ? 'input-opacity' : ''"
                v-model="secondaryCaregiverId"
                @input="soloCareGiver = false"
                @change="emptyMailPhone()">
                <option :value="0">- Välj epost/telefonnummer -</option>
                <option
                  v-for="s in secondaryCaregivers"
                  :value="s.secondaryCaregiverId"
                  :key="s.secondaryCaregiverId">
                  <span v-if="s.email && s.phoneNumber">{{ s.email + ' / ' + s.phoneNumber }}</span>
                  <span v-if="s.email && !s.phoneNumber">{{ s.email }}</span>
                  <span v-if="!s.email && s.phoneNumber">{{ s.phoneNumber }}</span>
                </option>
              </select>
            </div>
            <div class="col-sm-3">
              <div class="form-group">
                <label class="pull-left" for="email">E-post:</label>
                <input
                  type="text"
                  class="form-control"
                  id="email"
                  v-model="email"
                  @input="emptyCaregivers()"
                  :class="((email && email.length > 0) && !validEmail(email)) ? 'invalid' : ''"
                  :disabled="soloCareGiver || secondaryCaregiverId !== 0">
              </div>
            </div>
            <div class="col-sm-3">
              <div class="form-group">
                <label class="pull-left" for="phonenumber">Telefonnummer:</label>
                <input
                  type="text"
                  class="form-control"
                  id="phonenumber"
                  v-model="phonenumber"
                  :maxlength="phoneLength"
                  @input="enforceNumbers()"
                  :disabled="soloCareGiver || secondaryCaregiverId !== 0">
              </div>
            </div>
            <div class="col-sm-2">
              <label for="solocaregiver">Det finns endast en vårdnadshavare:</label>
              <input
                id="solocaregiver"
                type="checkbox"
                @input="secondaryCaregiverId = 0"
                @change="emptyMailPhone()"
                v-model="soloCareGiver"
                :disabled="email || phonenumber || secondaryCaregiverId !== 0">
              <br />
            </div>
          </div>
        </div>
      </div>
      <div class="clearfix"></div>
      <!-- Personuppgifter -->
      <div class="card journal-card" v-if="populationInfo">
        <div class="card-body">
          <p class="text-dark pull-left"><strong>Personuppgifter</strong></p>
          <div class="row">
            <div class="col-sm-2">
              <p class="card-title">Address</p>
              <p class="card-title">Postkod</p>
              <p class="card-title">Stad</p>
              <p class="card-title">C/O-adressering</p>
              <p class="card-title">Dödsdatum</p>
              <p class="card-title">Sekretessmarkering</p>
              <p class="card-title">Skyddad identitet</p>
            </div>
            <div class="col-sm-4">
              <p class="card-title">{{ toTitleCase(populationInfo.streetAddress) || '-' }}</p>
              <p class="card-title">{{ populationInfo.postalCode || '-' }}</p>
              <p class="card-title">{{ toTitleCase(populationInfo.city) || '-' }}</p>
              <p class="card-title">{{ populationInfo.careOf || 'Nej' }}</p>
              <p class="card-title">{{ populationInfo.deathDate || '-' }}</p>
              <p class="card-title"
                title="Hantera uppgifterna med försiktighet!"
                :class="populationInfo.hasNoteOfSecrecy === true ? 'text-danger': ''">
                  {{ boolToText(populationInfo.hasNoteOfSecrecy) }}
              </p>
              <p class="card-title"
                title="Hantera uppgifterna med försiktighet!"
                :class="populationInfo.hasProtectedIdentity === true ? 'text-danger': ''">
                  {{ boolToText(populationInfo.hasProtectedIdentity) }}
              </p>
            </div>
            <div class="col-sm-4"></div>
          </div>
        </div>
      </div>
      <div class="clearfix"></div>
      <!-- Screeningfrågor -->
      <div class="card journal-card">
        <div class="card-body">
          <p class="text-dark pull-left"><strong>Screeningfrågor</strong></p>
          <div class="row">
            <div class="col-sm-12 pull-left">
              <div class="form-group">
                <label v-html="data.screening.verifydiagnosisheader"></label>
                <br />
                <input type="radio" :value="true" v-model="hasDiagnosis" id="hasdiagnosis_yes">
                <label for="hasdiagnosis_yes">Ja</label>
                <br />
                <input type="radio" :value="false" v-model="hasDiagnosis" id="hasdiagnosis_no">
                <label for="hasdiagnosis_no">Nej</label>
                <div v-if="hasDiagnosis === true">
                  <div class="clearfix"></div>
                  <label>Om ja, ange årtal för diagnosställande:</label>
                  <select class="form-control m-w-220" v-model="diagnosisYear">
                    <option :value="0">Ange årtal</option>
                    <option v-for="year in years.slice().reverse()" :value="year" :key="year">{{ year }}</option>
                  </select>
                </div>
              </div>
            </div>
            <div class="col-sm"></div>
          </div>
          <hr />
          <div class="row">
            <div class="col-sm-12 pull-left">
              <div class="form-group">
                <label v-html="data.screening.verifygentestheader"></label>
                <br />
                <input type="radio" :value="true" v-model="hasGentest" id="hasgentest_yes">
                <label for="hasgentest_yes">Ja</label>
                <br />
                <input type="radio" :value="false" v-model="hasGentest" id="hasgentest_no">
                <label for="hasgentest_no">Nej</label>
                <div v-if="hasGentest">
                  <div class="clearfix"></div>
                  <label>Om ja, ange vårdinrättning:</label>
                  <select class="form-control m-w-270" v-model="carefacility">
                    <option :value="0">Ange vårdinrättning</option>
                    <option v-for="(region, facility) in data.misc.facilitylist" :value="facility" :key="facility">
                      {{ facility }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
            <div class="col-sm"></div>
          </div>
          <hr />
          <div class="row">
            <div class="col-sm-8 pull-left pull-left">
              <div class="form-group">
                <label v-html="data.screening.medicineHeader"></label>
                <tags-input
                  element-id="tags"
                  v-model="drugs"
                  placeholder=" Lägg till läkemedel"
                  :existing-tags="[
                    { key: 'd1', value: 'Atorvastatin (Atorbir, Lipitor, Lipistad)' },
                    { key: 'd2', value: 'Pravastatin' },
                    { key: 'd3', value: 'Simvastatin (Simidon, Zocord)' },
                    { key: 'd4', value: 'Rosuvastatin (Crestor)' },
                    { key: 'd5', value: 'Ezetemib (Ezetrol)' },
                    { key: 'd6', value: 'Alirokumab (Praluent)' },
                    { key: 'd7', value: 'Evolocumab (Repatha)' }
                  ]"
                  :typeahead-hide-discard="true"
                  :typeahead-always-show="true"
                  :typeahead="true"
                ></tags-input>
                <div v-for="drug in drugs" :key="drug.key">
                  <label for="drug">{{drug.value}}</label>
                  <input type="text"
                    placeholder="Mått: ex mg/ml"
                    maxlength="2"
                    class="form-control m-w-270"
                    v-model="drug.unit">
                  <input type="number"
                    placeholder="Dos: ex 10/20/40/80"
                    maxlength="3"
                    class="form-control m-w-270"
                    v-model.number="drug.dose">
                  <input type="number" placeholder="Antal per dag: ex 0.5/1/2"
                    v-if="drug.key !== 'd6' && drug.key !== 'd7'" class="form-control m-w-270" v-model.number="drug.amountperday">
                  <input type="number" placeholder="Antal inj varannan vecka: ex 1/2"
                    v-if="drug.key === 'd6' || drug.key === 'd7'" class="form-control m-w-270" v-model.number="drug.amountperday">
                  <div class="clearfix"></div>
                </div>
                <label for="nodrugs">Barnet tar inga läkemedel:</label> &nbsp;
                <input type="checkbox" @change="drugs = null" v-model="noDrugs" id="nodrugs">
              </div>
            </div>
            <div class="col-sm"></div>
          </div>
          <hr />
          <div class="row">
            <div class="col-sm-12 pull-left">
              <div class="form-group">
                <label v-html="data.screening.dietHeader"></label>
                <br />
                <input type="radio" :value="true" v-model="lchfDiet" id="lchfdiet_yes">
                <label for="lchfdiet_yes">Ja</label>
                <br />
                <input type="radio" :value="false" v-model="lchfDiet" id="lchfdiet_no">
                <label for="lchfdiet_no">Nej</label>
              </div>
            </div>
            <div class="col-sm"></div>
          </div>
          <hr />
          <div class="row">
            <div class="col-sm-12 pull-left">
              <div class="form-group">
                <label v-html="data.screening.eggheader"></label>
                <br />
                <input type="radio" v-model="eggs" id="less_than_14"
                  :value="{ answer: 'Mindre än 14 ägg per vecka', amount: 'less_than_14' }">
                  <label for="less_than_14">Mindre än 14 ägg per vecka (2 ägg/dag)</label>
                <br />
                <input type="radio" v-model="eggs" id="more_than_14"
                  :value="{ answer: 'Mer än 14 ägg per vecka', amount: 'more_than_14' }">
                  <label for="more_than_14">Mer än 14 ägg per vecka (2 ägg/dag)</label>
              </div>
            </div>
            <div class="col-sm">
              <div class="pull-left border-1" v-if="isDev">
                <label>Dev:</label>
                <input type="number" class="form-control" v-model.number="personId" placeholder="personId" />
                <div class="clearfix"></div>
                <button class="btn btn-primary btn-sm" @click="addChildrenUnder13ScreeningAnswers()">
                  childrenunder13/screeninganswers
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="clearfix"></div>
      <div class="alert alert-success alert-bottom-right alert-createnewminor-success">
        <strong>Registreringen av minderårigt barn är utfört.</strong>
      </div>
      <div class="alert alert-danger alert-bottom-right alert-createnewminor-error">
        <strong>{{error}}</strong>
      </div>
      <div class="btn-group">
        <button class="btn btn-lg btn-primary" @click="addChildrenUnder13PersonalData()" :disabled="!isFieldsValid">
          Registrera uppgifter
        </button>
        <button class="btn btn-lg btn-danger" @click="confirmLeave()">Avbryt</button>
      </div>
      <div class="clearfix"></div>
    </div>
  </div>
</template>

<script>
import config from '../../config/index.js'
import axios from '../plugins/axios'
import { request } from '../plugins/datocms'
import { v4 as uuidv4 } from 'uuid'
import { luhnSsn } from '../plugins/luhnssn'
import FieldId from '../plugins/fieldid'
import DatePick from 'vue-date-pick'
import 'vue-date-pick/dist/vueDatePick.css'
import $ from 'jquery'

export default {
  name: 'createnewminor',
  components: {
    DatePick
  },
  props: ['id'],
  data () {
    return {
      data: null,
      drugs: null,
      noDrugs: null,
      ssn: null,
      populationInfo: null,
      eggs: null,
      lchfDiet: null,
      phonenumber: null,
      email: null,
      indexPatientId: null,
      indexPatientName: null,
      personId: null,
      hasDiagnosis: null,
      hasGentest: null,
      soloCareGiver: false,
      diagnosisYear: 0,
      carefacility: 0,
      secondaryCaregiverId: 0,
      lastname: '',
      firstname: '',
      secondaryCaregivers: [],
      phoneLength: 10,
      error: 'Det gick inte att registrera minderårigt barn, vänligen försök igen om en stund.',
      isDev: process.env.NODE_ENV === 'dev'
    }
  },
  methods: {
    toTitleCase (phrase) {
      if (phrase) {
        return phrase
          .toLowerCase()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      }
    },
    boolToText (bool) {
      if (bool) {
        return 'Ja ⚠️'
      } else {
        return 'Nej'
      }
    },
    enforceNumbers () {
      this.soloCareGiver = false
      this.secondarycaregiver = 0
      this.phonenumber = this.phonenumber.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1')
    },
    validEmail (email) {
      const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return re.test(String(email).toLowerCase())
    },
    emptyMailPhone () {
      this.phonenumber = null
      this.email = null
    },
    emptyCaregivers () {
      this.soloCareGiver = false
      this.secondarycaregiver = 0
    },
    getFieldId (lbl) {
      return FieldId(lbl)
    },
    getFromNavet () {
      let loader = this.$loading.show()
      axios.postPopulationRegistry(config.populationRegistryUri, this.ssn).then((response) => {
        if (response) {
          this.populationInfo = response.data
          this.firstname = response.data.firstName !== null ? response.data.firstName : ''
          this.lastname = response.data.lastName !== null ? response.data.lastName : ''
          loader.hide()
        }
      }, (error) => {
        if (error.response && error.response.status === 401) {
          window.location.href = window.location.origin.concat('/error')
        } else {
          loader.hide()
          console.log(error)
        }
      })
    },
    showInvMethod (method) {
      if (method === 'sms') {
        return 'SMS'
      }
      if (method === 'email') {
        return 'E-post'
      }
    },
    confirmLeave () {
      const r = confirm('Är du säker? Du kan ha osparade ändringar.')
      if (r) {
        this.$router.go(-1)
      }
    },
    async getIdxPatient () {
      const url = config.indexPatientUri + '/byId?indexPatientId=' + this.$route.params.id
      axios._get(url).then(response => {
        this.indexPatientName = response.data.indexPatient.firstName + ' ' + response.data.indexPatient.lastName
      }).catch((error) => {
        if (error.response && error.response.status === 401) {
          window.location.href = window.location.origin.concat('/error')
        } else {
          console.log('Could not get indexpatient for potential new minor: ' + error)
        }
      })
    },
    async getSecondaryCaregivers () {
      if (this.indexPatientId) {
        axios._get(config.indexPatientUri + config.secondaryCaregiversUri + '?indexPatientId=' + this.indexPatientId).then(response => {
          this.secondaryCaregivers = response.data.secondaryCareGivers
        }).catch((error) => {
          if (error.response && error.response.status === 401) {
            window.location.href = window.location.origin.concat('/error')
          } else {
            console.log('Could not get secondary caregivers for indexpatient: ' + error)
          }
        })
      }
    },
    async addChildrenUnder13PersonalData () {
      const r = confirm('Är du säker på att du vill registrera minderårigt barn?')
      if (r) {
        const personalData = {
          ssn: this.ssn.toString(),
          firstname: this.firstname,
          lastname: this.lastname,
          email: this.email,
          phonenumber: this.phonenumber,
          familytreenode: uuidv4(),
          id: this.indexPatientId,
          solocaregiver: this.soloCareGiver,
          secondarycaregiverId: this.secondaryCaregiverId !== 0 ? this.secondaryCaregiverId : null
        }
        let loader = this.$loading.show()
        if (personalData) {
          axios.postChildrenUnder13PersonalData(config.childrenUnder13Uri + config.personalDataUri, personalData).then((response) => {
            if (response) {
              this.personId = response.data.personId
              if (this.personId) {
                this.addChildrenUnder13ScreeningAnswers()
              } else {
                loader.hide()
                $('.alert-createnewminor-error').show().delay(4000).fadeOut()
              }
            }
          }, (error) => {
            if (error.response && error.response.status === 401) {
              window.location.href = window.location.origin.concat('/error')
            } else {
              loader.hide()
              this.error = error.response.data ? error.response.data[0] : this.error
              $('.alert-createnewminor-error').show().delay(4000).fadeOut()
              console.log(error)
            }
          })
        }
      }
    },
    async addChildrenUnder13ScreeningAnswers () {
      let screeningAnswers = [
        {
          personId: this.personId,
          answer: JSON.stringify({
            question: 'Har du redan en FH-diagnos?',
            answer: (this.hasDiagnosis ? 'Ja' : 'Nej'),
            diagnosisyear: (this.diagnosisYear > 0) ? this.diagnosisYear : '',
            timestamp: new Date().toLocaleString('sv-SE', { timeZone: 'CET' })
          }),
          fieldId: 1722201 // screeningdiagnosis
        },
        {
          personId: this.personId,
          answer: JSON.stringify({
            question: 'Har din FH-diagnos bekräftats med ett genetiskt test?',
            answer: (this.hasGentest ? 'Ja' : 'Nej'),
            facility: (this.carefacility !== 0) ? this.carefacility : 'Vet ej',
            timestamp: new Date().toLocaleString('sv-SE', { timeZone: 'CET' })
          }),
          fieldId: 1722955 // screeninggentest
        },
        {
          personId: this.personId,
          answer: JSON.stringify({
            question: 'Har du en pågående behandling med kolesterolsänkande läkemedel?',
            answer: (this.noDrugs ? 'Nej' : 'Ja'),
            drugs: (this.noDrugs ? '' : this.convertPropInArray(this.drugs)),
            timestamp: new Date().toLocaleString('sv-SE', { timeZone: 'CET' })
          }),
          fieldId: 1293111 // screeningmedicine
        },
        {
          personId: this.personId,
          answer: JSON.stringify({
            question: 'Äter du en fettrik kost med få kolhydrater (t.ex. LCHF)?',
            answer: (this.lchfDiet ? 'Ja' : 'Nej'),
            timestamp: new Date().toLocaleString('sv-SE', { timeZone: 'CET' })
          }),
          fieldId: 1293109 // screeningdiet
        },
        {
          personId: this.personId,
          answer: JSON.stringify({
            question: 'Hur många ägg äter du en genomsnittlig vecka?',
            eggs: this.eggs,
            timestamp: new Date().toLocaleString('sv-SE', { timeZone: 'CET' })
          }),
          fieldId: 1293536 // screeningegg
        }
      ]
      let loader = this.$loading.show()
      if (screeningAnswers && screeningAnswers.length > 0) {
        axios.postChildrenUnder13ScreeningAnswers(config.childrenUnder13Uri + config.screeninganswersUri, screeningAnswers).then((response) => {
          if (response) {
            $('.alert-createnewminor-success').show().delay(4000).fadeOut()
            setTimeout(() => {
              loader.hide()
              window.location.href = window.location.origin.concat('/patients')
            }, 2000)
          }
        }, (error) => {
          if (error.response && error.response.status === 401) {
            window.location.href = window.location.origin.concat('/error')
          } else {
            loader.hide()
            this.error = error.response.data ? error.response.data[0] : this.error
            $('.alert-createnewminor-error').show().delay(4000).fadeOut()
            console.log(error)
          }
        })
      }
    },
    convertPropInArray (drugs) {
      if (drugs) {
        drugs = drugs.map(function (obj) {
          obj['name'] = obj['value']
          delete obj['value']
          return obj
        })
        return drugs
      }
    }
  },
  watch: {
    drugs (state) {
      if (state && state.length > 0) {
        this.noDrugs = false
      }
    }
  },
  computed: {
    years () {
      const year = new Date().getFullYear()
      return Array.from({length: year - 1900}, (value, index) => 1901 + index)
    },
    isFieldsValid () {
      if (this.validSsn &&
        (this.firstname && this.firstname.length !== 0) &&
        (this.lastname && this.lastname.length !== 0) &&
        (((this.email && this.email.length !== 0) ||
        this.phonenumber !== null) || this.soloCareGiver ||
        this.secondaryCaregiverId !== 0)) {
        return true
      }
    },
    validSsn () {
      const ssnValid = luhnSsn(this.ssn)
      return ssnValid
    }
  },
  async beforeMount () {
    this.indexPatientId = this.$route.params.id
    await this.getIdxPatient()
    await this.getSecondaryCaregivers()
  },
  async mounted () {
    this.data = await request({
      query: `
        {
          misc {
            facilitylist
          }
          clinicMisc {
            mutationlist
          }
          screening {
            dietHeader
            eggheader
            medicineHeader
            verifycontactheader
            verifydiagnosisheader
            verifygentestheader
            verifymutationheader
          }
        }
      `
    })
    if (this.data.screening) {
      this.data.screening = JSON.parse(
        JSON.stringify(this.data.screening)
          .replaceAll('du', 'barnet')
          .replaceAll('din', 'barnet')
          .replaceAll('patienten', 'barnet')
      )
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
h1, h2 {
  font-weight: normal;
}
ul {
  list-style-type: none;
  padding: 0;
}
li {
  display: inline-block;
  margin: 0 10px;
}
button:disabled {
  opacity: 0.3;
}
select:disabled {
  opacity: 0.5;
}
input[type=text]:disabled {
  opacity: 0.5;
}
input[type=checkbox] {
  transform: scale(1.5);
}
.input-opacity {
  opacity: 0.4;
  pointer-events: none;
}
.m-w-220 {
  max-width: 220px;
}
.m-w-270 {
  max-width: 270px;
}
.border-1 {
  border: 1px solid gray;
  max-width: 260px;
  padding: 10px;
}
.pull-left {
  text-align: left;
}
.pull-right {
  text-align: right;
}
.form-group-select {
  position: absolute;
  bottom: 0;
}
.invalid {
  border: 2px solid red;
}
.journal-card:hover {
  background: #b4c8cf;
  border: 1px solid #95a2a7;
  color: #000;
  opacity: 1.0;
}
.journal-card {
  background: #c5dae2;
  border: 1px solid #a9b6bb;
  color: #38362E;
  opacity: 0.8;
}
.journal-card-caregiver {
  opacity: 1.0 !important;
}
</style>



// WEBPACK FOOTER //
// src/components/CreateNewMinor.vue